import Big from 'big.js';
import { Tooltip } from 'bootstrap';
import { GetChildPrice, GetOptionPrice, GetPrice, GetPrices, GetSizes, GetTypes, PriceSheetMinQuantity, SizeNameMatches, TypeNameMatches } from 'smg/customer-api/lineitems';
import * as models from 'smg/customer-api/models';
import { DocumentTitleParts } from 'smg/dom';
import Modal from 'smg/modal';
import { CleanNumber } from 'smg/number';
import QueryString from 'smg/querystring';
import * as strings from 'smg/strings';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import { GetQuizRequest } from '../components/product-type-quiz';
import { GetMoldFee } from '../moldfees';
import { AddlUnitCosts, RequiredChildFloor, RequiredChildFloorOption, SkipChildren } from '../pricing';
import { QuoteModal } from '../quote';
import { SwiperInit } from '../swiper';

const MAX_PRESET_QUANTITY = 750;

let totalAddlUnitCostsBig: Big | undefined;

async function totalAddlUnitCosts(product: string): Promise<Big> {
    if (typeof totalAddlUnitCostsBig === 'undefined') {
        let total = Big(0);

        for (const addlUnitCost of AddlUnitCosts()) {
            if (addlUnitCost.cheapestOption) {
                total = total.plus(await RequiredChildFloor(product, addlUnitCost.child));
            } else if (addlUnitCost.option) {
                total = total.plus(await GetOptionPrice(product, addlUnitCost.child, addlUnitCost.option));
            } else {
                total = total.plus(await GetChildPrice(product, addlUnitCost.child));
            }
        }

        totalAddlUnitCostsBig = total;
    }

    return totalAddlUnitCostsBig;
}

void main();

function displaySize(size: models.LineItemTypeSize): string {
    const networkName = size.networkName();
    if (networkName) {
        return networkName;
    }

    let s = '';

    if (size.width.gt(0)) {
        s += `${strings.Trim(size.width.toString(), '0')} inch`;

        if (size.height.gt(0) && !size.width.eq(size.height)) {
            s += ' × ';
        }
    }

    if (size.height.gt(0) && !size.width.eq(size.height)) {
        s += `${strings.Trim(size.height.toString(), '0')} inch`;
    }

    if (size.type) {
        s += ` ${size.type}`;
    }

    return s;
}

type productName = 'Lapel Pins' | 'Challenge Coins' | 'Patches' | 'Lanyards' | 'Belt Buckles' | 'Key Chains' | 'Keychains' | 'Medals';

function shortProductSingular(product: productName): string {
    switch (product) {
    case 'Lapel Pins':
        return 'Pin';
    case 'Challenge Coins':
        return 'Coin';
    case 'Patches':
        return 'Patch';
    case 'Lanyards':
        return 'Lanyard';
    case 'Belt Buckles':
        return 'Buckle';
    case 'Key Chains':
    case 'Keychains':
        return 'Keychain';
    case 'Medals':
        return 'Medal';
    default:
        return product;
    }
}

function initMainImageContainer() {
    const $mainImageContainer = document.querySelector('#main-image-container') as HTMLElement;
    const $sliderImages = Array.from(document.querySelectorAll<HTMLElement>('[data-slider-image-id]'));
    $sliderImages.forEach($sliderImage => {
        $sliderImage.addEventListener('mouseover', () => {
            const $mainImage = document.querySelector<HTMLElement>(`#${$sliderImage.getAttribute('data-slider-image-id') || ''}`) as HTMLElement;
            for (const $child of Array.from($mainImageContainer.children)) {
                $child.classList.add('d-none');
            }
            $mainImage.classList.remove('d-none');
        });
    });
}

function initSwappableImageContainers() {
    document.querySelectorAll<HTMLElement>('.image-container-swappable').forEach($container => {
        const $mainImageContainer = $container.querySelector<HTMLElement>('.main-image-container') as HTMLElement;
        const $sliderImages = Array.from($container.querySelectorAll<HTMLElement>('[data-slider-image-id]'));
        $sliderImages.forEach($sliderImage => {
            $sliderImage.addEventListener('mouseover', () => {
                const $mainImage = $container.querySelector<HTMLElement>(`#${$sliderImage.getAttribute('data-slider-image-id') || ''}`) as HTMLElement;
                for (const $child of Array.from($mainImageContainer.children)) {
                    $child.classList.add('d-none');
                }
                $mainImage.classList.remove('d-none');
            });
        });
    });

    const bindGallerySwap = (inputName: string, dataAttr: string) => {
        document.querySelectorAll<HTMLInputElement>(`input[name="${inputName}"]`).forEach($radio => {
            $radio.addEventListener('click', () => {
                const $gallery = document.querySelector<HTMLElement>(`.image-container-swappable[${dataAttr}='${$radio.value}']`);
                if (!$gallery) {
                    return;
                }
                document.querySelectorAll<HTMLElement>('.image-container-swappable').forEach($container => {
                    $container.classList.add('d-none');
                });
                $gallery.classList.remove('d-none');
            });
        });
    };

    setTimeout(() => {
        bindGallerySwap('quote-size', 'data-size');
        bindGallerySwap('product-type', 'data-color');
    });
}

function syncThumbsHeightWithMain() {
    // Find the currently visible image container (either standard or swappable)
    const getVisibleContainer = () => {
        // First check for swappable containers
        const $visibleSwappable = document.querySelector<HTMLElement>('.image-container-swappable:not(.d-none)');
        if ($visibleSwappable) {
            return {
                $thumbs: $visibleSwappable.querySelector<HTMLElement>('.thumbs-container'),
                $mainContainer: $visibleSwappable.querySelector<HTMLElement>('.main-image-container'),
                getMain: () => $visibleSwappable.querySelector<HTMLElement>('.main-image:not(.d-none)'),
            };
        }

        // Fall back to standard container
        return {
            $thumbs: document.querySelector<HTMLElement>('#thumbs-container'),
            $mainContainer: document.querySelector<HTMLElement>('#main-image-container'),
            getMain: () => document.querySelector<HTMLElement>('#main-image-container .main-image:not(.d-none)'),
        };
    };

    const syncContainer = () => {
        const container = getVisibleContainer();
        if (!container.$thumbs || !container.$mainContainer) {
            return;
        }

        const setHeight = () => {
            const $main = container.getMain();
            if (!$main || !container.$thumbs) {
                return;
            }
            const h = Math.round($main.getBoundingClientRect().height);
            if (h > 0) {
                container.$thumbs.style.height = `${h}px`;
            }
        };

        // Coalesce rapid events to one layout per frame
        let scheduled = false;
        const scheduleSetHeight = () => {
            if (scheduled) {
                return;
            }
            scheduled = true;
            requestAnimationFrame(() => {
                scheduled = false;
                setHeight();
            });
        };

        setHeight();

        // Keep in sync with layout changes
        let resizeObserver: ResizeObserver | null = null;
        const attachResizeObserver = () => {
            // Disconnect previous observer if it exists
            if (resizeObserver) {
                resizeObserver.disconnect();
            }

            const $main = container.getMain();
            if ($main) {
                try {
                    resizeObserver = new ResizeObserver(() => scheduleSetHeight());
                    resizeObserver.observe($main);
                } catch {
                    // Ignore if ResizeObserver is unavailable
                }
            }
        };

        // Initial attachment
        attachResizeObserver();

        // Watch for image swaps by observing class changes on children
        try {
            const mutationObserver = new MutationObserver(() => {
                // Re-attach ResizeObserver to the new visible image
                attachResizeObserver();
                // Also update height immediately
                scheduleSetHeight();
            });

            mutationObserver.observe(container.$mainContainer, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['class'],
            });
        } catch {
            // Ignore if MutationObserver is unavailable
        }

        window.addEventListener('load', scheduleSetHeight);
        window.addEventListener('orientationchange', scheduleSetHeight);
        window.addEventListener('resize', scheduleSetHeight);
    };

    // Initialize for the current container
    syncContainer();

    // Watch for container visibility changes (when switching between size options)
    try {
        const containerObserver = new MutationObserver(() => {
            syncContainer();
        });

        // Observe all swappable containers for visibility changes
        document.querySelectorAll('.image-container-swappable').forEach($container => {
            containerObserver.observe($container, {
                attributes: true,
                attributeFilter: ['class'],
            });
        });
    } catch {
        // Ignore if MutationObserver is unavailable
    }
}

async function main() {
    const $sizeAndQuantity = document.querySelector('#quote-size-and-quantity') as HTMLElement;
    const $collapse = $sizeAndQuantity.closest('.collapse') as HTMLElement;
    const product = $collapse.dataset.product;
    let typeName = $collapse.dataset.type;
    const baseTypeName = $collapse.dataset.baseType ?? 'No Color';
    const maxPresetQty = parseInt($sizeAndQuantity.dataset.maxQty ?? '', 10) || MAX_PRESET_QUANTITY;

    const $sizeTemplate = document.querySelector('#quote-size-radio-template') as HTMLTemplateElement;
    const $sizesParent = document.querySelector('#quote-sizes-parent') as HTMLElement;

    let type: models.LineItemType | undefined;
    let sizes: models.LineItemTypeSize[] = [];

    const updateType = async (name: string) => {
        type = (await GetTypes(product ?? '')).find(t => TypeNameMatches(t, name));
        sizes = (await GetSizes(product ?? '', type?.name ?? '')).filter(s => (s.networkLineItemTypeSize?.prices ?? []).length > 0);

        typeName = type?.name;
    };
    await updateType(typeName ?? '');

    let currSize: models.LineItemTypeSize | undefined;
    let $firstSize: HTMLInputElement | null = null;
    let sizeSelected = false;

    let allowedSizes: Set<string> | undefined;
    if ($sizeAndQuantity.dataset.sizes) {
        allowedSizes = new Set($sizeAndQuantity.dataset.sizes.split(';'));
    }

    const sizeInAllowed = (size: models.LineItemTypeSize) => {
        if (!allowedSizes?.size) {
            return true;
        }
        for (const label of allowedSizes) {
            if (SizeNameMatches(size, label)) {
                return true;
            }
        }
        return false;
    };

    const $moldFeeEls = document.querySelectorAll<HTMLElement>('.mold-fee');

    if ($moldFeeEls.length) {
        const $tooltipContent = template.Clone('#tooltip-content-template').firstElementChild as HTMLElement;
        const $moldFeeTooltip = document.querySelector('.mold-fee-tooltip') as HTMLElement;
        const $closeBtn = $tooltipContent.querySelector('.close-btn') as HTMLElement;

        const getWindowSize = (): [number, number] => {
            if (window.innerWidth >= 768) {
                return [-250, 5];
            } else {
                const ms = (window.innerWidth - 513) / 2;
                return [-ms, 5];
            }
        };

        const $t = new Tooltip($moldFeeTooltip, {
            trigger: 'click',
            placement: 'top',
            title: $tooltipContent,
            html: true,
            template: '<div class="tooltip" role="tooltip"><div class="tooltip-arrow d-none"></div><div class="tooltip-inner"></div></div>',
            offset: getWindowSize(),
        });
        $closeBtn.addEventListener('click', () => {
            $t.hide();
        });
    }

    for (const size of sizes) {
        if (!(size.networkLineItemTypeSize?.prices ?? []).length) {
            continue;
        }

        if (!sizeInAllowed(size)) {
            continue;
        }

        const $size = template.Clone($sizeTemplate).firstElementChild as HTMLElement;
        const $radio = $size.querySelector('.form-check-input') as HTMLInputElement;
        $radio.value = size.networkName() || size.name;
        $radio.addEventListener('click', () => {
            setTimeout(() => {
                void showQtys();
            });
        });

        if (!$firstSize) {
            $firstSize = $radio;
        }

        const qsSize = QueryString().get('size') || '';
        if (qsSize && SizeNameMatches(size, qsSize)) {
            $radio.checked = true;
            $radio.dispatchEvent(new Event('click'));
            sizeSelected = true;
        }

        const $label = $size.querySelector('.form-check-text') as HTMLElement;
        $label.textContent = displaySize(size);

        $sizesParent.appendChild($size);
    }

    if ($firstSize && !sizeSelected) {
        $firstSize.checked = true;
        $firstSize.dispatchEvent(new Event('click'));
    }

    const getSize = () => {
        const sizeName = $sizesParent.querySelector<HTMLInputElement>('input[name="quote-size"]:checked')?.value ?? '';
        if (!sizeName) {
            currSize = undefined;
            return '';
        }
        const size = sizes.find(s => SizeNameMatches(s, sizeName));
        if (size) {
            currSize = size;
        } else {
            currSize = undefined;
        }
        return sizeName;
    };

    const $bulkPricing = document.querySelector('#quote-bulk-pricing-link') as HTMLAnchorElement;
    const href = new URL($bulkPricing.href);

    const updateBulkPricingHref = () => {
        const updatedHref = new URL(href.toString());
        updatedHref.searchParams.set('size', getSize());
        updatedHref.searchParams.set('qty', getQty().toString());

        $bulkPricing.href = updatedHref.toString();
    };

    const $qtyTemplate = document.querySelector('#quote-qty-radio-template') as HTMLTemplateElement;
    const $qtysParent = document.querySelector('#quote-qtys-parent') as HTMLElement;

    const $otherQtyContainer = document.querySelector('#quote-custom-qty-input-container') as HTMLElement;
    const $otherQty = $otherQtyContainer.querySelector('input') as HTMLInputElement;
    const $otherDecrement = $otherQtyContainer.querySelector('.decrement') as HTMLElement;
    const $otherIncrement = $otherQtyContainer.querySelector('.increment') as HTMLElement;

    $otherQty.addEventListener('input', async () => {
        await updateMoldFee();
        void updateTotal();
        void updateShapePricing();
        updateBulkPricingHref();
        updateTitle();
    });

    $otherDecrement.addEventListener('click', e => {
        e.preventDefault();
        $otherQty.value = String(Math.max(0, parseInt(CleanNumber($otherQty.value), 10) - 1));

        $otherQty.dispatchEvent(new Event('input'));
    });

    $otherIncrement.addEventListener('click', e => {
        e.preventDefault();
        $otherQty.value = String(parseInt(CleanNumber($otherQty.value), 10) + 1);

        $otherQty.dispatchEvent(new Event('input'));
    });

    const appendQty = (qty: number, price: Big, first: boolean, checked: boolean = first, other?: boolean) => {
        const $qty = template.Clone($qtyTemplate).firstElementChild as HTMLElement;
        const $radio = $qty.querySelector('.form-check-input') as HTMLInputElement;
        $radio.value = !other ? String(qty) : 'other';

        const $priceContainer = $qty.querySelector('.quote-qty-price-container') as HTMLElement;
        const $price = $qty.querySelector('.quote-qty-price') as HTMLElement;
        const $product = $qty.querySelector('.quote-qty-product') as HTMLElement;
        const $saveHr = $qty.querySelector('.quote-qty-save-hr') as HTMLElement;
        const $save = $qty.querySelector('.quote-qty-save') as HTMLElement;

        $price.textContent = !price.eq(0) ? DollarFormat(price) : '';
        $product.textContent = shortProductSingular(product as productName).toLowerCase();

        if (other) {
            $priceContainer.remove();
        } else if (first) {
            firstUnitPrice = price;

            $saveHr.classList.add('invisible');
            $save.remove();
        } else if (firstUnitPrice) {
            const percentOff = firstUnitPrice.minus(price).div(firstUnitPrice).times(100).round();
            $save.textContent = `SAVE ${percentOff.toString()}%`;
        }

        const $label = $qty.querySelector('.form-check-text') as HTMLElement;
        $label.textContent = !other ? qty.toLocaleString() : 'Other';

        $radio.addEventListener('click', async () => {
            if (other) {
                $otherQtyContainer.classList.remove('d-none');
                $otherQty.focus();
                $otherQty.select();
            } else {
                $otherQtyContainer.classList.add('d-none');
                $otherQty.value = qty.toString();

                updateTitle();
            }

            await updateMoldFee();
            void updateTotal();
            void updateTypePricing();
            void updateShapePricing();
            updateBulkPricingHref();
        });

        $qtysParent.appendChild($qty);

        if (checked) {
            $radio.checked = true;
            $radio.dispatchEvent(new Event('click'));
        }
    };

    function updateTitle() {
        const text = $sizeAndQuantity.hasAttribute('data-hide-size') ?
            `${getQty().toLocaleString()} Qty` :
            `${getSize()} | ${getQty().toLocaleString()} Qty`;
        const $selectedQuoteValue = document.querySelector('.selected-quote-value') as HTMLElement;
        $selectedQuoteValue.textContent = text;
    }

    function updateMultiSelectTitles($selectedQuoteValue: HTMLElement) {
        const checkedOptions = Array.from(document.querySelectorAll('input[type="checkbox"]:checked')).map(option => (option as HTMLInputElement).value);
        const newTitle = checkedOptions.join(', ');
        $selectedQuoteValue.textContent = newTitle;
    }

    const $spinner = $sizeAndQuantity.querySelector('.spinner-parent') as HTMLElement;
    const $sizeAndQuantityContent = $sizeAndQuantity.querySelector('.step-contents') as HTMLElement;

    let firstUnitPrice: Big | undefined;
    const showQtys = async () => {
        const rawPrevQty = getQty() || parseInt(CleanNumber(QueryString().get('qty') ?? '0'), 10);
        const prevQty = Number.isFinite(rawPrevQty) ? rawPrevQty : 0;
        $qtysParent.innerHTML = '';

        const minQty = await PriceSheetMinQuantity(product ?? '');
        const qtys = (await GetPrices(product ?? '', typeName ?? '', 0, undefined, undefined, getSize())).filter(qty => qty.quantity <= maxPresetQty && qty.quantity >= minQty);
        const addlPrice = await totalAddlUnitCosts(product ?? '');
        let prevQtyInSet = false;

        for (let i = 0; i < qtys.length; i++) {
            const qty = qtys[i];

            if (prevQty === qty.quantity) {
                prevQtyInSet = true;
            }

            appendQty(qty.quantity, qty.price.add(addlPrice), !i, prevQty ? prevQty === qty.quantity : undefined);
        }

        const otherChecked = prevQty ? !prevQtyInSet : qtys.length === 0;
        appendQty(0, Big(0), false, otherChecked, true);
        if (otherChecked) {
            $otherQty.value = prevQty ? prevQty.toString() : '';
            $otherQty.dispatchEvent(new Event('input'));
        }

        $spinner.remove();
        $sizeAndQuantityContent.classList.remove('d-none');
    };

    const getQty = () => {
        const $checked = $qtysParent.querySelector<HTMLInputElement>('input[name="quote-qty"]:checked');
        if (!$checked) {
            return 0;
        }
        const parsed = $checked.value === 'other' ?
            parseInt(CleanNumber($otherQty.value || '0'), 10) :
            parseInt(CleanNumber($checked.value || '0'), 10);

        if (!Number.isFinite(parsed) || parsed < 0) {
            return 0;
        }

        return parsed;
    };

    const $options = document.querySelectorAll<HTMLInputElement>('.quote-step-option');
    $options.forEach($option => $option.addEventListener('click', () => {
        void updateTotal();
        if ($option.type === 'radio') {
            const $quoteStepParent = $option.closest('.quote-step-parent') as HTMLElement;
            const $selectedQuoteValue = $quoteStepParent.querySelector('.selected-quote-value') as HTMLElement;
            if ($selectedQuoteValue) {
                $selectedQuoteValue.textContent = $option.value;
                $selectedQuoteValue.title = $option.value;
            }

            // When size is hidden, Shape option drives the gallery and pricing size
            if ($option.name === 'Shape' && $sizeAndQuantity.hasAttribute('data-hide-size')) {
                const $matchingSize = Array.from($sizesParent.querySelectorAll<HTMLInputElement>('input[name="quote-size"]')).
                    find(r => r.value === $option.value);

                // Always switch the gallery (independent of whether the radio changed)
                document.querySelectorAll<HTMLElement>('.image-container-swappable').forEach($c => $c.classList.add('d-none'));
                if ($matchingSize) {
                    document.querySelector<HTMLElement>(`.image-container-swappable[data-size='${$option.value}']`)?.classList.remove('d-none');
                } else {
                    // No Preference: restore the default (all) gallery
                    document.querySelector<HTMLElement>('.image-container-swappable:not([data-size])')?.classList.remove('d-none');
                }

                // Only re-trigger pricing if the size actually changed
                if ($matchingSize && !$matchingSize.checked) {
                    $matchingSize.click();
                } else if (!$matchingSize && $firstSize && !$firstSize.checked) {
                    // No Preference: reset pricing to the first/base size
                    $firstSize.click();
                }
            }
        } else if ($option.type === 'checkbox') {
            const $quoteStepParent = $option.closest('.quote-step-parent') as HTMLElement;
            const $selectedQuoteValue = $quoteStepParent.querySelector('.selected-quote-value') as HTMLElement;

            updateMultiSelectTitles($selectedQuoteValue);
        }
    }));

    const $types = document.querySelectorAll<HTMLInputElement>('.product-type-option');
    $types.forEach($type => $type.addEventListener('click', async () => {
        await updateType($type.value);
        await updateTotal();
        await showQtys();
    }));

    const updateMoldFee = async () => {
        if (!$moldFeeEls.length) {
            return;
        }

        const moldFee = await GetMoldFee(product ?? '', type?.id ?? '', currSize?.id ?? '', currSize?.width.toNumber() ?? 0, currSize?.height.toNumber() ?? 0, getQty());
        $moldFeeEls.forEach($moldFee => {
            $moldFee.textContent = DollarFormat(moldFee);
            $moldFee.dataset.price = moldFee.toString();
        });
    };

    const $includedChildren = document.querySelectorAll<HTMLElement>('.included-child');

    const $total = document.querySelectorAll<HTMLElement>('.quote-total');
    const updateTotal = async () => {
        let qty = getQty();
        const minQty = $qtysParent.querySelector<HTMLInputElement>('input.form-check-input');
        const minQtyParsed = parseInt(minQty?.value || '0', 10);
        if (Number.isFinite(minQtyParsed)) {
            qty = Math.max(qty, minQtyParsed);
        }

        let price = await GetPrice(product ?? '', typeName ?? '', getSize(), qty);
        price = price.plus(await totalAddlUnitCosts(product ?? ''));

        const $prices: HTMLElement[] = [];

        for (const $option of Array.from($options)) {
            if ($option.checked) {
                const $label = $option.closest('label.form-control') as HTMLElement;
                $prices.push(...Array.from($label.querySelectorAll<HTMLElement>('.child-line-item-option-price[data-price],.child-line-item-price[data-price]')));
            }
        }

        $includedChildren.forEach($includedChild => {
            $prices.push($includedChild);
        });

        for (const $price of $prices) {
            if ($price.hasAttribute('data-single')) {
                continue;
            }

            price = price.plus(Big($price.dataset.price || 0));
        }

        let total = price.times(qty);

        for (const $price of $prices) {
            if (!$price.hasAttribute('data-single')) {
                continue;
            }

            total = total.plus(Big($price.dataset.price || 0));
        }

        document.querySelectorAll<HTMLElement>('.additional-one-time-fee').forEach($fee => {
            total = total.plus(Big($fee.dataset.price || 0));
        });

        $moldFeeEls.forEach($moldFee => {
            total = total.plus(Big($moldFee.dataset.price || 0));
        });

        $total.forEach($total => {
            $total.textContent = DollarFormat(total);
        });
    };

    const updateTypePricing = async () => {
        const $typePricing = document.querySelectorAll<HTMLElement>('.line-item-type-price');
        const minPrice = GetTypes(product ?? '').then(types => {
            const foundType = types.find(t => TypeNameMatches(t, baseTypeName));
            const selectedSize = currSize || foundType?.lineItemTypeSizes?.[0];
            const size = foundType?.lineItemTypeSizes?.find(s =>
                selectedSize ? SizeNameMatches(s, selectedSize.networkName()) || SizeNameMatches(s, selectedSize.name) : false,
            );
            const currQty = getQty();
            let price = Big(0);
            for (const p of size?.networkLineItemTypeSize?.prices ?? []) {
                if (p.quantity <= currQty) {
                    price = Big(p.price);
                }
            }
            return price;
        });

        await minPrice.then(minPrice => {
            $typePricing.forEach(async $typePricing => {
                const lineItem = $typePricing.getAttribute('data-line-item');
                const type = $typePricing.getAttribute('data-type');
                const free = $typePricing.getAttribute('data-free');
                const template = $typePricing.getAttribute('data-template');
                if (!lineItem || !type) {
                    return;
                }

                let price: Big;

                if (type === baseTypeName) {
                    price = minPrice;
                } else {
                    price = await GetTypes(lineItem).then(types => {
                        const foundType = types.find(t => TypeNameMatches(t, type));
                        const selectedSize = currSize || foundType?.lineItemTypeSizes?.[0];
                        const size = foundType?.lineItemTypeSizes?.find(s =>
                            selectedSize ? SizeNameMatches(s, selectedSize.networkName()) || SizeNameMatches(s, selectedSize.name) : false,
                        );
                        const currQty = getQty();
                        let price = Big(0);
                        for (const p of size?.networkLineItemTypeSize?.prices ?? []) {
                            if (p.quantity <= currQty) {
                                price = Big(p.price);
                            }
                        }
                        return price;
                    });
                }

                const adjPrice = price.minus(minPrice);

                if ($typePricing.hasAttribute('data-free') && adjPrice.eq(0)) {
                    $typePricing.textContent = free;
                    return;
                }

                if (template) {
                    $typePricing.textContent = template.replace('$', DollarFormat(adjPrice));
                } else {
                    $typePricing.textContent = DollarFormat(adjPrice);
                }

                $typePricing.setAttribute('data-price', adjPrice.toString());
            });
        });
    };

    const updateShapePricing = async () => {
        const $shapePricing = Array.from(document.querySelectorAll<HTMLElement>('.shape-size-price[data-size]'));
        if (!$shapePricing.length) {
            return;
        }

        const qty = getQty();
        const sizeNames = Array.from(new Set($shapePricing.map($el => $el.dataset.size ?? '').filter(Boolean)));

        const priceBySizeName = new Map<string, Big>(
            await Promise.all(sizeNames.map(async sizeName => [sizeName, await GetPrice(product ?? '', typeName ?? '', sizeName, qty)] as const)),
        );

        let minPrice: Big | undefined;
        for (const p of priceBySizeName.values()) {
            if (!minPrice || p.lt(minPrice)) {
                minPrice = p;
            }
        }
        if (!minPrice) {
            return;
        }

        for (const $el of $shapePricing) {
            const sizeName = $el.dataset.size ?? '';
            const price = priceBySizeName.get(sizeName);
            if (!price) {
                continue;
            }

            const diff = price.minus(minPrice);
            const free = $el.dataset.free ?? '';
            const tmpl = $el.dataset.template ?? '';

            if (diff.eq(0)) {
                $el.textContent = free;
            } else if (tmpl) {
                $el.textContent = tmpl.replace('$', DollarFormat(diff));
            } else {
                $el.textContent = DollarFormat(diff);
            }
        }
    };

    void showQtys();

    const $getQuote = document.querySelectorAll<HTMLElement>('.quote-get,.quote-open');
    $getQuote.forEach($getQuote => $getQuote.addEventListener('click', async e => {
        e.preventDefault();

        // Every required radio group — product-type color cards and
        // quote-step option cards (Finish, Shape, …) alike — needs a
        // selection before the quote modal opens.
        const $requiredRadios = Array.from(document.querySelectorAll<HTMLInputElement>('.product-type-option, .quote-step-option')).filter(r => r.required);
        const requiredNames: string[] = [];
        for (const $radio of $requiredRadios) {
            if (!requiredNames.includes($radio.name)) {
                requiredNames.push($radio.name);
            }
        }
        for (const name of requiredNames) {
            const $group = $requiredRadios.filter(r => r.name === name);
            if ($group.some(r => r.checked)) {
                continue;
            }
            const $step = $group[0].closest('.quote-step-parent') as HTMLElement | null;
            if ($step) {
                const $collapse = $step.querySelector<HTMLElement>('.collapse');
                if ($collapse && !$collapse.classList.contains('show')) {
                    const $toggler = $step.querySelector<HTMLElement>('.collapse-toggler');
                    $toggler?.click();
                }
                $step.scrollIntoView({ behavior: 'smooth', block: 'center' });
                $step.classList.add('quote-step-highlight');
                window.setTimeout(() => $step.classList.remove('quote-step-highlight'), 1600);
            }
            return;
        }

        const children: models.QuoteRequestCreateChild[] = [];
        const extras: models.QuoteRequestCreateExtra[] = [];
        const tradingPin = $getQuote.dataset.tradingpin?.toLowerCase();

        let sportSelected = false;
        let otherSportSelected = false;
        $options.forEach($option => {
            if (($option.checked && $option.name === 'Sport') && !($option.value === 'Other')) {
                sportSelected = true;
            } else if ($option.checked && $option.name === 'Sport' && $option.value === 'Other') {
                otherSportSelected = true;
            }
        });

        extras.push(new models.QuoteRequestCreateExtra({
            name: 'Product Page',
            value: document.body.dataset.title,
        }));

        const $selectedType = document.querySelector<HTMLInputElement>('.product-type-option:checked');
        if ($selectedType) {
            const $titleEl = $selectedType.closest('label')?.querySelector<HTMLElement>('.fw-700');
            extras.push(new models.QuoteRequestCreateExtra({
                name: 'Color',
                value: $titleEl?.textContent?.trim() || $selectedType.value,
            }));
        }

        for (const addlUnitCost of AddlUnitCosts()) {
            // cheapestOption: if customer selected a card, their selection flows
            // via $options below; otherwise send a floor-priced placeholder.
            if (addlUnitCost.cheapestOption) {
                const childSelected = Array.from(document.querySelectorAll<HTMLInputElement>('.quote-step-option:checked')).some($option => {
                    const $card = $option.closest('.swiper-slide');
                    if (!$card) {
                        return false;
                    }
                    return Array.from($card.querySelectorAll<HTMLElement>('.child-line-item-option-price[data-child]')).some($span => {
                        const spanChild = $span.getAttribute('data-child') ?? '';
                        return spanChild.localeCompare(addlUnitCost.child, undefined, { sensitivity: 'base' }) === 0;
                    });
                });
                if (childSelected) {
                    continue;
                }
                const cheapestOptionName = await RequiredChildFloorOption(product ?? '', addlUnitCost.child);
                children.push(new models.QuoteRequestCreateChild({
                    child: addlUnitCost.child,
                    option: cheapestOptionName,
                    cheapestOption: true,
                }));
                continue;
            }
            children.push(new models.QuoteRequestCreateChild({
                child: addlUnitCost.child,
                option: addlUnitCost.option,
            }));
        }

        for (const child of SkipChildren()) {
            children.push(new models.QuoteRequestCreateChild({
                child,
                skip: true,
            }));
        }

        $includedChildren.forEach($includedChild => {
            children.push(new models.QuoteRequestCreateChild({
                child: $includedChild.dataset.child ?? '',
                option: $includedChild.dataset.option ?? '',
            }));
        });

        document.querySelectorAll<HTMLElement>('.additional-one-time-fee').forEach($fee => {
            children.push(new models.QuoteRequestCreateChild({
                child: $fee.dataset.child ?? '',
                option: $fee.dataset.option ?? '',
            }));
        });

        $options.forEach($option => {
            if ($option.checked) {
                if ($option.name === 'Template' || $option.name === 'Shape') {
                    extras.push(new models.QuoteRequestCreateExtra({
                        name: $option.name,
                        value: $option.value,
                    }));
                } else if ($option.name === 'Sport' && !otherSportSelected) {
                    extras.push(new models.QuoteRequestCreateExtra({
                        name: 'Sport',
                        value: $option.value,
                    }));
                } else if ($option.name === 'Sport' && otherSportSelected) {
                    return;
                } else {
                    const $label = $option.closest('label.form-control') as HTMLElement;
                    const $price = $label.querySelectorAll<HTMLElement>('.child-line-item-option-price,.child-line-item-price');
                    $price.forEach($price => {
                        let option = $price.dataset.option ?? '';
                        if ($option.classList.contains('skip-option-in-quote')) {
                            option = '';
                        }

                        children.push(new models.QuoteRequestCreateChild({
                            child: $price.dataset.child ?? '',
                            option,
                        }));
                    });
                }
            }
        });

        const quiz = GetQuizRequest();
        let quizNotes = '';
        if (quiz) {
            quizNotes = 'I filled out the quiz and these were my answers:\n\n';
            quizNotes += quiz.questions.map(q => `${q.question}:\n${q.answer}\n\n`).join('');
        }

        QuoteModal({
            lineItem: product ?? '',
            size: getSize(),
            type: typeName ?? '',
            quantity: getQty(),
            children,
            extras,
            prefixNotes: quizNotes,
            disableCompany: tradingPin !== undefined,
            teamFields: tradingPin !== undefined,
            sport: tradingPin === 'true' && !sportSelected,
            otherSport: tradingPin === 'true' && otherSportSelected,
            form: `product detail (${document.body.dataset.title || DocumentTitleParts().title})`,
            sku: $getQuote.dataset.sku,
            backButtonText: 'Close',
        });
    }));

    const $quoteActions = document.querySelector('#quote-actions') as HTMLElement;

    const getActionsSpaceBelow = () => {
        const bounds = $quoteActions.getBoundingClientRect();
        return window.innerHeight - bounds.bottom;
    };

    const updateStickyShadow = () => {
        const stuck = getActionsSpaceBelow() <= 1; // treat as stuck when pinned to bottom
        $quoteActions.classList.toggle('is-stuck', stuck);
        const $quoteActionsCard = $quoteActions.querySelector('.card') as HTMLElement | null;
        if ($quoteActionsCard) {
            if (stuck) {
                $quoteActionsCard.classList.remove('shadow-lt-5');
                $quoteActionsCard.classList.add('shadow-lt-8');
            } else {
                $quoteActionsCard.classList.add('shadow-lt-5');
                $quoteActionsCard.classList.remove('shadow-lt-8');
            }
        }
    };

    const getPodiumWidget = () => document.querySelector<HTMLElement>('#podium-website-widget');

    const getPodiumHeight = () => {
        let maxTopFromBottom: number | undefined;
        let minBottom: number | undefined;

        document.querySelectorAll<HTMLElement>('#podium-website-widget > iframe').forEach($iframe => {
            const bounds = $iframe.getBoundingClientRect();

            const topFromBottom = bounds.height + bounds.bottom;
            if (maxTopFromBottom === undefined || topFromBottom > maxTopFromBottom) {
                maxTopFromBottom = topFromBottom;
            }

            if (minBottom === undefined || bounds.bottom > minBottom) {
                minBottom = bounds.bottom;
            }
        });

        return (maxTopFromBottom ?? 0) - (minBottom ?? 0);
    };

    let ticking = false;
    const updatePodiumOffset = () => {
        if (!ticking) {
            window.requestAnimationFrame(() => {
                const actionsSpaceBelow = getActionsSpaceBelow();
                const podiumHeight = getPodiumHeight();

                if (actionsSpaceBelow < podiumHeight) {
                    getPodiumWidget()?.classList.add('opacity-0', 'pe-none');
                } else {
                    getPodiumWidget()?.classList.remove('opacity-0', 'pe-none');
                }

                updateStickyShadow();

                ticking = false;
            });

            ticking = true;
        }
    };

    window.addEventListener('resize', updatePodiumOffset);
    window.addEventListener('scroll', updatePodiumOffset);

    updatePodiumOffset();
    updateStickyShadow();

    // Show product detail explainer modal on first visit
    const $explainerTemplate = document.querySelector('#product-detail-explainer-template') as HTMLTemplateElement;
    if ($explainerTemplate && window.localStorage.getItem('product-detail-explainer-shown') !== 'true') {
        new Modal({
            body: template.Clone($explainerTemplate).firstElementChild as HTMLElement,
            width: '34rem',
            appendConfirmButtonClass: 'w-100 btn-lg mx-auto',
            confirmText: 'Sounds Good!',
            cancelButton: false,
            escape: true,
            title: '\u00A0', // Non-breaking space to keep header structure
            appendClassBySelector: {
                '.modal-body': 'px-6 pt-4 pb-8 mt-n4', // Added mt-n4 to pull content up
                '.modal-footer': 'px-6 pt-4 pb-6',
                '.modal-title': 'opacity-0', // Hide title but keep space
                '.modal-header': 'px-6 pt-6 pb-0 border-0',
                '.btn-close': 'opacity-75',
            },
            onClose: () => {
                window.localStorage.setItem('product-detail-explainer-shown', 'true');
            },
        });
    }

    if (document.querySelector('.image-container-swappable')) {
        initSwappableImageContainers();
    } else {
        initMainImageContainer();
    }

    // Keep thumbs column matched to main image height
    syncThumbsHeightWithMain();
}

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll<HTMLElement>('.swiper-button-prev, .swiper-button-next').forEach($btn => {
        $btn.style.visibility = 'visible';
        $btn.style.pointerEvents = 'auto';
    });
    SwiperInit();
});
